/**
 * WC-A2 — in-app ownership digest (Layer A morning summary).
 * Replaces email-thread morning updates per docs/admin_paperwork.md.
 */

export const OWNERSHIP_DIGEST_KIND = "ownership_digest";

export type OwnershipDigestSchedule = {
  /** Local HH:mm for future cron pilot; manual send OK for v1. */
  local_send_time?: string | null;
  encouraging_note?: string | null;
  /** When true (default), all active org members receive the digest row. */
  broadcast_all_members?: boolean;
  last_sent_at?: string | null;
  /** YYYY-MM-DD report day last delivered (dedupe). */
  last_digest_day?: string | null;
};

export type DigestLeaderboardRow = {
  user_id: string;
  display_name: string;
  count: number;
};

export type OwnershipDigestStats = {
  report_day: string;
  prior_day_sent_count: number;
  prior_day_by_rep: DigestLeaderboardRow[];
  mtd_leaderboard: DigestLeaderboardRow[];
  closed_deals_prior_day: number;
};

export type OwnershipDigestContent = {
  title: string;
  body: string;
  plain_text: string;
  stats: OwnershipDigestStats;
};

const DEFAULT_ENCOURAGING =
  "Great work yesterday — keep the momentum going today. Share your sales callouts in-app when you close a deal.";

export function normalizeOwnershipDigestSchedule(raw: unknown): OwnershipDigestSchedule {
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const local_send_time =
    typeof o.local_send_time === "string" && /^\d{2}:\d{2}$/.test(o.local_send_time.trim())
      ? o.local_send_time.trim()
      : null;
  const encouraging_note =
    typeof o.encouraging_note === "string" && o.encouraging_note.trim()
      ? o.encouraging_note.trim().slice(0, 2000)
      : null;
  return {
    local_send_time,
    encouraging_note,
    broadcast_all_members: o.broadcast_all_members !== false,
    last_sent_at: typeof o.last_sent_at === "string" ? o.last_sent_at : null,
    last_digest_day: typeof o.last_digest_day === "string" ? o.last_digest_day : null,
  };
}

export function utcDayBounds(reportDay: string): { start: string; end: string } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(reportDay)) return null;
  const [y, m, d] = reportDay.split("-").map(Number);
  const start = new Date(Date.UTC(y!, m! - 1, d!, 0, 0, 0, 0));
  const end = new Date(Date.UTC(y!, m! - 1, d!, 23, 59, 59, 999));
  return { start: start.toISOString(), end: end.toISOString() };
}

export function monthStartUtc(reportDay: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(reportDay)) return null;
  const [y, m] = reportDay.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, 1, 0, 0, 0, 0)).toISOString();
}

export function priorCalendarDayUtc(referenceMs: number = Date.now()): string {
  const d = new Date(referenceMs);
  d.setUTCDate(d.getUTCDate() - 1);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function formatReportDayLabel(reportDay: string): string {
  const bounds = utcDayBounds(reportDay);
  if (!bounds) return reportDay;
  return new Date(bounds.start).toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function buildOwnershipDigestContent(input: {
  stats: OwnershipDigestStats;
  encouragingNote?: string | null;
}): OwnershipDigestContent {
  const label = formatReportDayLabel(input.stats.report_day);
  const title = `Daily ownership digest — ${label}`;

  const lines: string[] = [];
  lines.push(`Summary for ${label}`);
  lines.push("");
  lines.push(
    `Prior-day sales: ${input.stats.prior_day_sent_count} lead${input.stats.prior_day_sent_count === 1 ? "" : "s"} marked sent`
  );
  if (input.stats.prior_day_by_rep.length > 0) {
    for (const row of input.stats.prior_day_by_rep.slice(0, 12)) {
      lines.push(`• ${row.display_name} — ${row.count}`);
    }
  } else {
    lines.push("• No sent leads recorded for this day.");
  }
  lines.push("");
  lines.push("Month-to-date leaderboard (sent leads):");
  if (input.stats.mtd_leaderboard.length > 0) {
    input.stats.mtd_leaderboard.slice(0, 10).forEach((row, idx) => {
      lines.push(`${idx + 1}. ${row.display_name} — ${row.count}`);
    });
  } else {
    lines.push("No sent leads yet this month.");
  }
  if (input.stats.closed_deals_prior_day > 0) {
    lines.push("");
    lines.push(`Ops deals closed prior day: ${input.stats.closed_deals_prior_day}`);
  }
  const note = (input.encouragingNote ?? DEFAULT_ENCOURAGING).trim();
  lines.push("");
  lines.push(note);
  lines.push("");
  lines.push(
    "Sales callouts: reply in-app via workplace messaging or your daily workflow — email reply is not required in v1."
  );

  const body = lines.join("\n");
  const plain_text = body;

  return { title, body, plain_text, stats: input.stats };
}

export function aggregateSentLeadsByOwner(
  rows: readonly { owner_user_id?: string | null }[],
  nameByUser: ReadonlyMap<string, string>
): DigestLeaderboardRow[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const uid = typeof row.owner_user_id === "string" ? row.owner_user_id.trim() : "";
    if (!uid) continue;
    counts.set(uid, (counts.get(uid) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([user_id, count]) => ({
      user_id,
      display_name: nameByUser.get(user_id) ?? user_id.slice(0, 8),
      count,
    }))
    .sort((a, b) => b.count - a.count || a.display_name.localeCompare(b.display_name));
}
